import type { ActionFunctionArgs } from "react-router-dom";
import { tierAPI, APIError } from "../../lib/api";
import { createTierSchema, updateTierSchema } from "../../schemas";
import { extractFieldErrors, parseFormData } from "../../utils";
import type { ActionResult } from "./auth.actions";

export async function createTierAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId) {
    return { success: false, error: "Organization ID and Network ID are required" };
  }

  const formData = await parseFormData(request);

  const result = createTierSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const tier = await tierAPI.create(orgId, networkId, {
      name: result.data.name,
      description: result.data.description || undefined,
      monthlyPriceCents: result.data.monthlyPriceCents,
      annualPriceCents: result.data.annualPriceCents,
    });
    return { success: true, data: tier };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function updateTierAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId, tierId } = params;

  if (!orgId || !networkId || !tierId) {
    return { success: false, error: "Organization ID, Network ID, and Tier ID are required" };
  }

  const formData = await parseFormData(request);

  const result = updateTierSchema.safeParse(formData);

  if (!result.success) {
    return { success: false, fieldErrors: extractFieldErrors(result.error) };
  }

  try {
    const tier = await tierAPI.update(orgId, networkId, tierId, {
      name: result.data.name,
      description: result.data.description || undefined,
      monthlyPriceCents: result.data.monthlyPriceCents,
      annualPriceCents: result.data.annualPriceCents,
    });
    return { success: true, data: tier };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function deleteTierAction({ params }: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId, tierId } = params;

  if (!orgId || !networkId || !tierId) {
    return { success: false, error: "Organization ID, Network ID, and Tier ID are required" };
  }

  try {
    await tierAPI.delete(orgId, networkId, tierId);
    return { success: true };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}

export async function reorderTiersAction({
  request,
  params,
}: ActionFunctionArgs): Promise<ActionResult> {
  const { id: orgId, networkId } = params;

  if (!orgId || !networkId) {
    return { success: false, error: "Organization ID and Network ID are required" };
  }

  try {
    const formData = await request.formData();
    const tierIdsJson = formData.get("tierIds");
    if (!tierIdsJson || typeof tierIdsJson !== "string") {
      return { success: false, error: "Tier IDs are required" };
    }

    const tierIds = JSON.parse(tierIdsJson) as string[];
    const tiers = await tierAPI.reorder(orgId, networkId, tierIds);
    return { success: true, data: tiers };
  } catch (error) {
    if (error instanceof APIError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "An unexpected error occurred" };
  }
}
